import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Spin, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import RolesPermissionsModal from '../../../../components/modelpopup/RolesPermissionsModal';
import {
  useGetRolesQuery,
  useGetRoleQuery,
  useGetPermissionsQuery,
  useUpdateRolePermissionsMutation,
} from '../../../../services/settings.service';
import { ROLES } from '../../../../services/ENDPOINTS';
import { useDispatch } from 'react-redux';
import { toggleDelete } from '../../../../redux/slices/ui.slice';

const RolesPermissions = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const dispatch = useDispatch();

  // API Hooks
  const { data: rolesData, isLoading: rolesLoading } = useGetRolesQuery();
  const { data: selectedRoleData, isLoading: roleLoading } = useGetRoleQuery(selectedRoleId, {
    skip: !selectedRoleId,
  });
  const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery();
  const [updateRolePermissions, { isLoading: updating }] = useUpdateRolePermissionsMutation();

  const roles = rolesData?.data || [];
  const permissions = permissionsData?.data || [];
  const selectedRole = selectedRoleData?.data;

  // Set first role as selected on load
  useEffect(() => {
    if (roles.length > 0 && !selectedRoleId) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  // Update local permissions when role data changes
  useEffect(() => {
    if (selectedRole && selectedRole.permissions) {
      setRolePermissions(selectedRole.permissions || {});
    }
  }, [selectedRole]);

  // Handle Add Role
  const handleAdd = () => {
    setEditingRole(null);
    setModalVisible(true);
  };

  // Handle Edit Role
  const handleEdit = (role) => {
    setEditingRole(role);
    setModalVisible(true);
  };

  // Handle Delete Role
  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Role',
        url: `${ROLES}/${item.id}`,
        ele: e.currentTarget,
        parent: 'li'
      })
    );
  };

  // Handle Role Selection
  const handleRoleSelect = (roleId) => {
    setSelectedRoleId(roleId);
  };

  // Handle Permission Toggle
  const handlePermissionToggle = async (moduleId, action, checked) => {
    const updatedPermissions = { ...rolePermissions };

    if (!updatedPermissions[moduleId]) {
      updatedPermissions[moduleId] = {};
    }

    updatedPermissions[moduleId][action] = checked;

    setRolePermissions(updatedPermissions);

    try {
      await updateRolePermissions({
        roleId: selectedRoleId,
        permissions: updatedPermissions
      }).unwrap();
      message.success('Permission updated successfully');
    } catch (error) {
      message.error(error?.data?.message || 'Failed to update permission');
      // Revert on error
      setRolePermissions(selectedRole?.permissions || {});
    }
  };

  // Handle Module Access Toggle
  const handleModuleToggle = async (moduleId, checked) => {
    const updatedPermissions = { ...rolePermissions };

    if (checked) {
      // Enable all permissions for this module
      updatedPermissions[moduleId] = {
        read: true,
        write: true,
        create: true,
        delete: true,
        import: true,
        export: true,
      };
    } else {
      // Disable all permissions for this module
      delete updatedPermissions[moduleId];
    }

    setRolePermissions(updatedPermissions);

    try {
      await updateRolePermissions({
        roleId: selectedRoleId,
        permissions: updatedPermissions
      }).unwrap();
      message.success(`${checked ? 'Enabled' : 'Disabled'} module access`);
    } catch (error) {
      message.error(error?.data?.message || 'Failed to update module access');
      // Revert on error
      setRolePermissions(selectedRole?.permissions || {});
    }
  };

  // Check if module is active
  const isModuleActive = (moduleId) => {
    return rolePermissions[moduleId] && Object.values(rolePermissions[moduleId]).some(val => val);
  };

  // Get permission value
  const getPermission = (moduleId, action) => {
    return rolePermissions[moduleId]?.[action] || false;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Roles & Permissions</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Settings</li>
                </ul>
              </div>
              <div className="col-auto float-end ms-auto">
                <Link
                  to="#"
                  className="btn add-btn"
                  onClick={handleAdd}
                >
                  <i className="fa fa-plus" />
                  Add Role
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Roles Sidebar */}
            <div className="col-sm-4 col-md-4 col-lg-4 col-xl-3">
              <div className="roles-menu">
                {rolesLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                  </div>
                ) : (
                  <ul>
                    {roles.map((role) => (
                      <li
                        key={role.id}
                        className={selectedRoleId === role.id ? 'active' : ''}
                      >
                        <Link to="#" onClick={() => handleRoleSelect(role.id)}>
                          {role.name}
                          <span className="role-action">
                            <span
                              className="action-circle large me-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(role);
                              }}
                            >
                              <i className="material-icons">edit</i>
                            </span>
                            <span
                              className="action-circle large delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                doDelete(e, role);
                              }}
                            >
                              <i className="material-icons">delete</i>
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Permissions Panel */}
            <div className="col-sm-8 col-md-8 col-lg-8 col-xl-9">
              {roleLoading || permissionsLoading || updating ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                </div>
              ) : selectedRole ? (
                <>
                  <h6 className="card-title m-b-20">
                    Module Access for {selectedRole.name}
                  </h6>

                  {/* Module Access Toggles */}
                  <div className="m-b-30">
                    <ul className="list-group notification-list">
                      {permissions.map((module) => (
                        <li key={module.id} className="list-group-item">
                          {module.name}
                          <div className="status-toggle">
                            <input
                              type="checkbox"
                              id={`module_${module.id}`}
                              className="check"
                              checked={isModuleActive(module.id)}
                              onChange={(e) => handleModuleToggle(module.id, e.target.checked)}
                            />
                            <label htmlFor={`module_${module.id}`} className="checktoggle">
                              checkbox
                            </label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Permissions Table */}
                  <div className="table-responsive">
                    <table className="table table-striped custom-table">
                      <thead>
                        <tr>
                          <th>Module Permission</th>
                          <th className="text-center">Read</th>
                          <th className="text-center">Write</th>
                          <th className="text-center">Create</th>
                          <th className="text-center">Delete</th>
                          <th className="text-center">Import</th>
                          <th className="text-center">Export</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissions.map((module) => (
                          <tr key={module.id}>
                            <td>{module.name}</td>
                            {['read', 'write', 'create', 'delete', 'import', 'export'].map((action) => (
                              <td key={action} className="text-center">
                                <label className="custom_check">
                                  <input
                                    type="checkbox"
                                    checked={getPermission(module.id, action)}
                                    onChange={(e) =>
                                      handlePermissionToggle(module.id, action, e.target.checked)
                                    }
                                  />
                                  <span className="checkmark" />
                                </label>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#888' }}>
                  <p>Select a role to view and manage permissions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role Modal */}
      <RolesPermissionsModal
        visible={modalVisible}
        role={editingRole}
        onClose={() => {
          setModalVisible(false);
          setEditingRole(null);
        }}
        onSuccess={(newRole) => {
          setModalVisible(false);
          setEditingRole(null);
          if (newRole && newRole.id) {
            setSelectedRoleId(newRole.id);
          }
        }}
      />
    </>
  );
};

export default RolesPermissions;
